/**
 * Capture image from video element to canvas and convert to blob
 * Default to 9:16 portrait aspect ratio for 4K portrait displays
 * @param {HTMLVideoElement} videoElement - Video element to capture from
 * @param {number} width - Canvas width (portrait: narrower dimension)
 * @param {number} height - Canvas height (portrait: taller dimension)
 * @returns {Promise<Blob>} - Image blob
 */
export async function captureVideoFrame(videoElement, width = 1080, height = 1920) {
  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoElement, 0, 0, width, height);

      // Use JPEG at 85% quality instead of PNG - much smaller file size
      // PNG 1080x1920 = ~2MB, JPEG 85% = ~200-400KB
      canvas.toBlob((blob) => {
        if (blob) {
          console.log('Captured image size:', (blob.size / 1024).toFixed(0), 'KB');
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      }, 'image/jpeg', 0.85);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Upload image blob to server
 * @param {Blob} blob - Image blob to upload
 * @param {string} uploadUrl - Upload endpoint URL
 * @returns {Promise<string>} - Public URL of uploaded image
 */
export async function uploadImage(blob, uploadUrl) {
  const formData = new FormData();
  formData.append('image', blob, 'selfie.jpg');

  const response = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }

  const data = await response.json();
  return data.url;
}

/**
 * Request camera access with specific constraints
 * Default to 9:16 portrait aspect ratio for 4K portrait displays
 * @param {Object} constraints - Media constraints
 * @returns {Promise<MediaStream>} - Camera stream
 */
export async function requestCamera(constraints = {
  video: {
    width: { ideal: 1080 },
    height: { ideal: 1920 },
    aspectRatio: { ideal: 9/16 },
    facingMode: 'user'
  }
}) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    return stream;
  } catch (error) {
    console.error('Camera access error:', error);
    throw error;
  }
}

/**
 * Stop all tracks in a media stream
 * @param {MediaStream} stream - Stream to stop
 */
export function stopMediaStream(stream) {
  if (stream) {
    stream.getTracks().forEach(track => track.stop());
  }
}
